import React, { useRef, useState } from "react";
import { Form, Input, Select, message } from "antd";

const { Option } = Select;

// Declare global types for Google Maps
declare global {
  interface Window {
    google: any;
  }
}

// Type definitions for Google Maps (simplified)
type GoogleMapsAutocomplete = any;
type GoogleMapsPlace = any;

interface LoadScriptProps {
  googleMapsApiKey: string;
  libraries: string[];
  onLoad?: () => void;
  children: React.ReactNode;
}

interface AutocompleteProps {
  onLoad: (autocomplete: GoogleMapsAutocomplete) => void;
  onPlaceChanged: () => void;
  options?: any;
  children: React.ReactElement;
}

// Lazy import components (will be available after package installation)
let LoadScript: React.FC<LoadScriptProps>;
let Autocomplete: React.FC<AutocompleteProps>;

try {
  const googleMapsApi = require("@react-google-maps/api");
  LoadScript = googleMapsApi.LoadScript;
  Autocomplete = googleMapsApi.Autocomplete;
} catch (e) {
  // Package not installed yet
  LoadScript = ({ children }: LoadScriptProps) => <>{children}</>;
  Autocomplete = ({ children }: AutocompleteProps) => <>{children}</>;
}

const libraries: ("places")[] = ["places"];

interface AddressInputProps {
  form?: any;
  disabled?: boolean;
}

const SA_PROVINCES = [
  "Eastern Cape",
  "Free State",
  "Gauteng",
  "KwaZulu-Natal",
  "Limpopo",
  "Mpumalanga",
  "Northern Cape",
  "North West",
  "Western Cape",
];

const AddressInput: React.FC<AddressInputProps> = ({ form, disabled = false }) => {
  const autocompleteRef = useRef<GoogleMapsAutocomplete | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

  const onLoad = (autocomplete: GoogleMapsAutocomplete) => {
    autocompleteRef.current = autocomplete;
  };

  const onPlaceChanged = () => {
    if (autocompleteRef.current && form) {
      const place: GoogleMapsPlace = autocompleteRef.current.getPlace();

      if (!place.address_components) {
        message.warning("Please select an address from the dropdown");
        return;
      }

      // Parse address components
      let streetNumber = "";
      let route = "";
      let suburb = "";
      let city = "";
      let province = "";
      let postalCode = "";

      place.address_components.forEach((component: any) => {
        const types = component.types;

        if (types.includes("street_number")) {
          streetNumber = component.long_name;
        }
        if (types.includes("route")) {
          route = component.long_name;
        }
        if (types.includes("sublocality") || types.includes("sublocality_level_1")) {
          suburb = component.long_name;
        }
        if (types.includes("locality")) {
          city = component.long_name;
        }
        if (types.includes("administrative_area_level_1")) {
          province = component.long_name;
        }
        if (types.includes("postal_code")) {
          postalCode = component.long_name;
        }
      });

      // Build Address Line 1
      const addressLine1 = streetNumber && route 
        ? `${streetNumber} ${route}` 
        : route || place.name || "";

      // Map province to standard SA province name
      const mappedProvince = mapToSAProvince(province);

      // Update form fields
      form.setFieldsValue({
        AddressLine1: addressLine1,
        Suburb: suburb,
        CityTown: city,
        Province: mappedProvince,
        PostalCode: postalCode,
      });

      message.success("Address auto-filled successfully!");
    }
  };

  const mapToSAProvince = (googleProvince: string): string => {
    const provinceMap: { [key: string]: string } = {
      "Eastern Cape": "Eastern Cape",
      "Free State": "Free State",
      "Gauteng": "Gauteng",
      "GP": "Gauteng",
      "KwaZulu-Natal": "KwaZulu-Natal",
      "KZN": "KwaZulu-Natal",
      "Limpopo": "Limpopo",
      "Mpumalanga": "Mpumalanga",
      "Northern Cape": "Northern Cape",
      "North West": "North West",
      "Western Cape": "Western Cape",
      "WC": "Western Cape",
    };

    return provinceMap[googleProvince] || googleProvince || "";
  };

  if (!apiKey) {
    return (
      <>
        <Form.Item label="Search Address">
          <Input
            size="large"
            disabled
            placeholder="Google Maps API key not configured"
          />
        </Form.Item>
        {renderAddressFields(disabled)}
      </>
    );
  }

  return (
    <LoadScript
      googleMapsApiKey={apiKey}
      libraries={libraries}
      onLoad={() => setIsLoaded(true)}
    >
      <Form.Item
        label="Search Address"
        tooltip="Start typing your address to get suggestions"
      >
        {isLoaded ? (
          <Autocomplete
            onLoad={onLoad}
            onPlaceChanged={onPlaceChanged}
            options={{
              componentRestrictions: { country: "za" },
              fields: ["address_components", "formatted_address", "name"],
            }}
          >
            <Input
              size="large"
              placeholder="Start typing your address (e.g., '123 Jan Smuts Avenue, Sandton')"
              disabled={disabled}
            />
          </Autocomplete>
        ) : (
          <Input
            size="large"
            placeholder="Loading Google Maps..."
            disabled
          />
        )}
      </Form.Item>

      {renderAddressFields(disabled)}
    </LoadScript>
  );
};

const renderAddressFields = (disabled: boolean) => (
  <>
    <Form.Item
      name="AddressLine1"
      label="Address Line 1"
      rules={[
        {
          required: true,
          message: "Please enter Address Line 1",
        },
      ]}
    >
      <Input
        size="large"
        placeholder="Street number and name"
        disabled={disabled}
      />
    </Form.Item>

    <Form.Item
      name="AddressLine2"
      label="Address Line 2"
    >
      <Input
        size="large"
        placeholder="Apartment, suite, unit, building, floor, etc. (optional)"
        disabled={disabled}
      />
    </Form.Item>

    <Form.Item
      name="Suburb"
      label="Suburb"
      rules={[
        {
          required: true,
          message: "Please enter Suburb",
        },
      ]}
    >
      <Input
        size="large"
        placeholder="Suburb"
        disabled={disabled}
      />
    </Form.Item>

    <Form.Item
      name="CityTown"
      label="City/Town"
      rules={[
        {
          required: true,
          message: "Please enter City/Town",
        },
      ]}
    >
      <Input
        size="large"
        placeholder="City/Town"
        disabled={disabled}
      />
    </Form.Item>

    <Form.Item
      name="PostalCode"
      label="Postal Code"
      rules={[
        {
          required: true,
          message: "Please enter Postal Code",
        },
        {
          pattern: /^\d{4}$/,
          message: "Postal code must be 4 digits",
        },
      ]}
    >
      <Input
        size="large"
        placeholder="Postal Code"
        disabled={disabled}
      />
    </Form.Item>

    <Form.Item
      name="Province"
      label="Province"
      rules={[
        {
          required: true,
          message: "Please select Province",
        },
      ]}
    >
      <Select
        size="large"
        placeholder="Select Province"
        disabled={disabled}
        showSearch
        filterOption={(input, option) =>
          String(option?.children || "").toLowerCase().includes(input.toLowerCase())
        }
      >
        {SA_PROVINCES.map((province) => (
          <Option key={province} value={province}>
            {province}
          </Option>
        ))}
      </Select>
    </Form.Item>
  </>
);

export default AddressInput;
